import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiCall } from '@/utils/api';

// Types
interface Allocation {
  id: string;
  epicId: string;
  programId: string;
  teamId: string;
  teamWeeks: number;
  scenarioId?: string;
  epic?: {
    id: string;
    name: string;
    status: string;
    investibleStructure?: {
      category: string;
    };
  };
  program?: {
    id: string;
    name: string;
  };
  team?: {
    id: string;
    name: string;
    costProfile?: {
      hourlyRate: number;
    };
  };
}

interface AllocationsState {
  allocations: Allocation[];
  currentAllocation: Allocation | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

const initialState: AllocationsState = {
  allocations: [],
  currentAllocation: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

// Async thunks
export const fetchAllocations = createAsyncThunk(
  'allocations/fetchAll',
  async (params: { scenarioId?: string; epicId?: string; programId?: string; teamId?: string } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.scenarioId) queryParams.append('scenarioId', params.scenarioId);
    if (params.epicId) queryParams.append('epicId', params.epicId);
    if (params.programId) queryParams.append('programId', params.programId);
    if (params.teamId) queryParams.append('teamId', params.teamId);

    const response = await apiCall('GET', `/allocations?${queryParams}`);
    return response.data?.allocations || response.data || [];
  }
);

export const createAllocation = createAsyncThunk(
  'allocations/create',
  async (data: {
    epicId: string;
    programId: string;
    teamId: string;
    teamWeeks: number;
    scenarioId?: string;
  }) => {
    const response = await apiCall('POST', '/allocations', data);
    return response.data;
  }
);

export const updateAllocation = createAsyncThunk(
  'allocations/update',
  async ({ id, data }: { id: string; data: Partial<Allocation> }) => {
    const response = await apiCall('PUT', `/allocations/${id}`, data);
    return response.data;
  }
);

export const deleteAllocation = createAsyncThunk(
  'allocations/delete',
  async (id: string) => {
    await apiCall('DELETE', `/allocations/${id}`);
    return id;
  }
);

export const bulkUpdateAllocations = createAsyncThunk(
  'allocations/bulkUpdate',
  async (updates: Array<{ id: string; teamWeeks: number }>) => {
    const response = await apiCall('PUT', '/allocations/bulk-update', { updates });
    return response.data;
  }
);

// Slice
const allocationsSlice = createSlice({
  name: 'allocations',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    setCurrentAllocation(state, action: PayloadAction<Allocation | null>) {
      state.currentAllocation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch allocations
      .addCase(fetchAllocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllocations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allocations = action.payload;
      })
      .addCase(fetchAllocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch allocations';
      })
      // Create allocation
      .addCase(createAllocation.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createAllocation.fulfilled, (state, action) => {
        state.isCreating = false;
        state.allocations.push(action.payload);
      })
      .addCase(createAllocation.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create allocation';
      })
      // Update allocation
      .addCase(updateAllocation.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateAllocation.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.allocations.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.allocations[index] = action.payload;
        }
      })
      .addCase(updateAllocation.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message || 'Failed to update allocation';
      })
      // Delete allocation
      .addCase(deleteAllocation.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteAllocation.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.allocations = state.allocations.filter((a) => a.id !== action.payload);
      })
      .addCase(deleteAllocation.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.error.message || 'Failed to delete allocation';
      })
      // Bulk update allocations
      .addCase(bulkUpdateAllocations.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(bulkUpdateAllocations.fulfilled, (state) => {
        state.isUpdating = false;
        // Refetch allocations to get updated data
      })
      .addCase(bulkUpdateAllocations.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message || 'Failed to bulk update allocations';
      });
  },
});

export const { clearError, setCurrentAllocation } = allocationsSlice.actions;
export default allocationsSlice.reducer;
